import WidgetTrustpilot from '@components/WidgetTrustpilot';
import { LayoutContext } from '@context/LayoutContext';
import { IFooterPaymentReinsurance } from '@context/LayoutContext/types';
import useI18n from '@hooks/i18n/useI18n';
import EcoLeaf from '@monofront/design-system/visuals/Icons/Old/EcoLeaf';
import HeartLaFourche from '@monofront/design-system/visuals/Icons/Old/HeartLaFourche';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import { useTranslations } from 'next-intl';
import { useContext } from 'react';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { FEATURE_FLAG_TRUSTPILOT_FOOTER_TEMPLATE } from '@monofront/business/constants';

interface IFooterReinsuranceProps {
  hasAlcoholDisclaimer?: boolean;
}

const FooterReinsurance: React.FC<IFooterReinsuranceProps> = ({
  hasAlcoholDisclaimer = false,
}) => {
  const { isFrenchMarket, isGermanMarket } = useI18n();
  const { footer } = useContext(LayoutContext);

  const trustpilotTemplate = useFeatureValue<string>(
    FEATURE_FLAG_TRUSTPILOT_FOOTER_TEMPLATE,
    '',
  );

  const tGlobal = useTranslations('Global');
  const paymentReinsurance = footer?.find(
    (slice) => slice.slice_type === 'footer_payment_reinsurance',
  ) as IFooterPaymentReinsurance;

  return (
    <>
      <section className="footer-miscellaneous">
        {paymentReinsurance && (
          <div className="footer-payment">
            <h3>{paymentReinsurance.primary.title}</h3>
            <ul>
              {paymentReinsurance.items.map(({ logo }, index) =>
                logo ? (
                  <li key={index}>
                    <ResponsiveImage
                      alt={logo.alt || ''}
                      src={logo.url}
                      width={logo.dimensions.width}
                      height={logo.dimensions.height}
                      raw
                    />
                  </li>
                ) : null,
              )}
            </ul>
          </div>
        )}
        {trustpilotTemplate !== '' && (
          <div className="trustpilot">
            <WidgetTrustpilot
              templateId={trustpilotTemplate}
              height="28px"
              width="100%"
              theme="light"
            />
          </div>
        )}
        {trustpilotTemplate === '' && <div />}
        <div className="copyright">
          <p>
            {tGlobal('copyright')} {new Date().getFullYear()}
          </p>
          <p className="copyright-logo">
            {isGermanMarket && (
              <HeartLaFourche
                width={24}
                height={20}
                role="img"
                aria-label={tGlobal('companyName')}
              />
            )}
            {isGermanMarket && (
              <EcoLeaf
                width={36}
                height={24}
                role="img"
                aria-label={tGlobal('bioEurope')}
              />
            )}
            {isFrenchMarket && (
              <ResponsiveImage
                src="/icons/societeAMission.svg"
                height={40}
                width={76}
                role="img"
                aria-label={tGlobal('impactCompany')}
              />
            )}
          </p>
        </div>
        {isFrenchMarket && hasAlcoholDisclaimer && (
          <div className="alcohol-disclaimer">
            <ResponsiveImage
              src="/images/alcohol-disclaimer.png"
              alt={tGlobal('alcoholDisclaimerShort')}
              width={587}
              aria-describedby="alcohol-full-disclaimer"
              raw
            />
            <p id="alcohol-full-disclaimer" className="sr-only">
              {tGlobal('alcoholDisclaimer')}
            </p>
          </div>
        )}
      </section>
      <style jsx>{`
        .footer-miscellaneous {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          grid-gap: var(--space-5);
        }

        .footer-payment h3 {
          margin-bottom: var(--space-2);
          font-size: var(--font-size-2);
        }

        .footer-payment ul li {
          display: inline-block;
          margin-right: var(--space-1);
        }
        .footer-payment ul li :global(img) {
          height: 24px;
          width: auto;
        }

        .footer-miscellaneous :global(.trustpilot-widget) {
          // -70% = small hack to properly position the truspilot component
          margin: 0 -70%;
        }

        .copyright {
          text-align: right;
        }

        .copyright-logo {
          margin-top: var(--space-2);
        }

        .copyright-logo :global(svg) {
          margin-left: var(--space-2);
        }

        .alcohol-disclaimer {
          grid-area: 2 / 1 / 3 / 4;
          text-align: center;
        }

        .alcohol-disclaimer :global(img) {
          max-width: 100%;
        }

        @media (max-width: 800px) {
          .footer-miscellaneous {
            display: block;
          }
          .footer-miscellaneous > div {
            margin-bottom: var(--space-5);
          }
          .footer-miscellaneous .copyright {
            text-align: initial;
          }
          .footer-miscellaneous .trustpilot {
            margin-left: var(--negative-space-3);
            // 250px = small hack to properly position the truspilot component
            width: 250px;
          }
          .footer-miscellaneous :global(.trustpilot-widget) {
            margin: 0;
          }
        }
      `}</style>
    </>
  );
};

export default FooterReinsurance;
